import React, { useEffect, useState, useRef, useMemo } from "react";
import { PortalService } from "services/portal";
import Chart from "chart.js/auto";
import {
  TrendingUp, TrendingDown, ShoppingCart, Receipt, Wallet, Scale,
} from "lucide-react";

const CARD = { background: "var(--bg-card)", borderRadius: 14, border: "1px solid var(--border)" };
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const COLORS = {
  compras: "#6366f1",       // indigo
  comprasSoft: "rgba(99,102,241,0.12)",
  ventas: "#10b981",        // emerald
  ventasSoft: "rgba(16,185,129,0.12)",
  margen: "#0ea5e9",        // sky
  margenSoft: "rgba(14,165,233,0.12)",
};

const fmtCLP = (n) => "$" + Math.round(n || 0).toLocaleString("es-CL");
const fmtShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(n || 0);
};

function KpiCard({ icon: Icon, label, value, sub, accent, deltaPct }) {
  const positiveDelta = deltaPct !== undefined && deltaPct >= 0;
  return (
    <div style={{ ...CARD, padding: 18, position: "relative", overflow: "hidden" }}>
      <div style={{
        position: "absolute", top: 0, left: 0, height: 3, width: "100%",
        background: accent || "#0353a4",
      }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: (accent || "#0353a4") + "1a",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon size={18} color={accent || "#0353a4"} />
        </div>
        {deltaPct !== undefined && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
            background: positiveDelta ? "#ecfdf5" : "#fef2f2",
            color: positiveDelta ? "#059669" : "#dc2626",
          }}>
            {positiveDelta ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {positiveDelta ? "+" : ""}{deltaPct.toFixed(1)}%
          </span>
        )}
      </div>
      <p style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1, marginBottom: 6, fontVariantNumeric: "tabular-nums" }}>{value}</p>
      {sub && <p style={{ fontSize: 12, color: "var(--text-muted)" }}>{sub}</p>}
    </div>
  );
}

// Mini-barra inline para celdas de la tabla mensual.
function BarCell({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: "var(--bg-hover)", borderRadius: 999, overflow: "hidden", minWidth: 60 }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.3s" }} />
      </div>
      <span style={{ fontSize: 12, color: "var(--text-secondary)", fontVariantNumeric: "tabular-nums", minWidth: 80, textAlign: "right" }}>
        {fmtCLP(value)}
      </span>
    </div>
  );
}

export function PortalConsolidadoAnual() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(currentYear);
  const [prevData, setPrevData] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const barRef = useRef(null);
  const lineRef = useRef(null);
  const barInst = useRef(null);
  const lineInst = useRef(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      PortalService.getResumen({ year }).catch(() => null),
      PortalService.getResumen({ year: year - 1 }).catch(() => null),
    ])
      .then(([cur, prev]) => {
        setData(Array.isArray(cur) ? { resumen: cur } : cur || { resumen: [] });
        setPrevData(Array.isArray(prev) ? { resumen: prev } : prev || { resumen: [] });
      })
      .finally(() => setLoading(false));
  }, [year]);

  const aggregate = (rows) => {
    const acc = {
      comprasNeto: 0, comprasIva: 0, comprasMonto: 0, comprasDocs: 0,
      ventasNeto: 0, ventasIva: 0, ventasMonto: 0, ventasDocs: 0,
    };
    for (const r of rows || []) {
      if (r.compras) {
        acc.comprasNeto += r.compras.totalNeto || 0;
        acc.comprasIva += r.compras.totalIva || 0;
        acc.comprasMonto += r.compras.totalMonto || 0;
        acc.comprasDocs += r.compras.cantidadDocumentos || 0;
      }
      if (r.ventas) {
        acc.ventasNeto += r.ventas.totalNeto || 0;
        acc.ventasIva += r.ventas.totalIva || 0;
        acc.ventasMonto += r.ventas.totalMonto || 0;
        acc.ventasDocs += r.ventas.cantidadDocumentos || 0;
      }
    }
    return {
      ...acc,
      balance: acc.ventasNeto - acc.comprasNeto,
      ivaPagar: Math.max(0, acc.ventasIva - acc.comprasIva),
      remanente: Math.max(0, acc.comprasIva - acc.ventasIva),
    };
  };

  const totals = useMemo(() => aggregate(data?.resumen || []), [data]);
  const prevTotals = useMemo(() => aggregate(prevData?.resumen || []), [prevData]);

  const delta = (a, b) => (b > 0 ? ((a - b) / b) * 100 : (a > 0 ? 100 : undefined));

  // Barras: compras vs ventas por mes.
  useEffect(() => {
    if (!barRef.current) return;
    if (barInst.current) barInst.current.destroy();

    const rows = data?.resumen || [];
    const compras = Array.from({ length: 12 }, (_, i) => rows.find((x) => x.month === i + 1)?.compras?.totalMonto || 0);
    const ventas = Array.from({ length: 12 }, (_, i) => rows.find((x) => x.month === i + 1)?.ventas?.totalMonto || 0);

    barInst.current = new Chart(barRef.current, {
      type: "bar",
      data: {
        labels: MONTHS_SHORT,
        datasets: [
          { label: "Compras", data: compras, backgroundColor: COLORS.compras, borderRadius: 6, borderSkipped: false, maxBarThickness: 22 },
          { label: "Ventas",  data: ventas,  backgroundColor: COLORS.ventas,  borderRadius: 6, borderSkipped: false, maxBarThickness: 22 },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top", align: "end",
            labels: { usePointStyle: true, pointStyle: "circle", padding: 16, font: { size: 12, weight: "500" }, color: "var(--text-secondary)" },
          },
          tooltip: {
            backgroundColor: "#0f172a", titleColor: "#fff", bodyColor: "#e2e8f0",
            padding: 12, borderRadius: 8, displayColors: true, usePointStyle: true,
            callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtCLP(ctx.raw)}` },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: fmtShort, color: "var(--text-muted)", font: { size: 11 } },
            grid: { color: "#f1f5f9", drawBorder: false },
            border: { display: false },
          },
          x: {
            grid: { display: false },
            ticks: { color: "var(--text-secondary)", font: { size: 11, weight: "500" } },
            border: { display: false },
          },
        },
      },
    });
    return () => { if (barInst.current) barInst.current.destroy(); };
  }, [data]);

  // Línea: margen (ventas − compras) por mes.
  useEffect(() => {
    if (!lineRef.current) return;
    if (lineInst.current) lineInst.current.destroy();

    const rows = data?.resumen || [];
    const margen = Array.from({ length: 12 }, (_, i) => {
      const r = rows.find((x) => x.month === i + 1);
      return (r?.ventas?.totalNeto || 0) - (r?.compras?.totalNeto || 0);
    });

    lineInst.current = new Chart(lineRef.current, {
      type: "line",
      data: {
        labels: MONTHS_SHORT,
        datasets: [{
          label: "Margen neto",
          data: margen,
          borderColor: COLORS.margen,
          backgroundColor: COLORS.margenSoft,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: "#fff",
          pointBorderColor: COLORS.margen,
          pointBorderWidth: 2,
          borderWidth: 2.5,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#0f172a", titleColor: "#fff", bodyColor: "#e2e8f0",
            padding: 12, borderRadius: 8,
            callbacks: { label: (ctx) => `Margen: ${fmtCLP(ctx.raw)}` },
          },
        },
        scales: {
          y: {
            ticks: { callback: fmtShort, color: "var(--text-muted)", font: { size: 11 } },
            grid: { color: "#f1f5f9", drawBorder: false },
            border: { display: false },
          },
          x: {
            grid: { display: false },
            ticks: { color: "var(--text-secondary)", font: { size: 11, weight: "500" } },
            border: { display: false },
          },
        },
      },
    });
    return () => { if (lineInst.current) lineInst.current.destroy(); };
  }, [data]);

  const monthRow = (m) => {
    const r = (data?.resumen || []).find((x) => x.month === m);
    const c = r?.compras || {};
    const v = r?.ventas || {};
    return {
      monthIdx: m,
      comprasNeto: c.totalNeto || 0,
      comprasIva: c.totalIva || 0,
      ventasNeto: v.totalNeto || 0,
      ventasIva: v.totalIva || 0,
      diferencia: (v.totalNeto || 0) - (c.totalNeto || 0),
    };
  };

  const monthlyRows = useMemo(() => Array.from({ length: 12 }, (_, i) => monthRow(i + 1)), [data]);
  const maxMonth = Math.max(1, ...monthlyRows.map((r) => Math.max(r.comprasNeto, r.ventasNeto)));

  const yearOptions = useMemo(() => [currentYear, currentYear - 1, currentYear - 2, currentYear - 3], [currentYear]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4, letterSpacing: -0.3 }}>
            Consolidado anual
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>
            Tu actividad de compras y ventas en {year}, con comparación contra {year - 1}.
          </p>
        </div>
        <div style={{ display: "inline-flex", background: "var(--bg-hover)", borderRadius: 999, padding: 4, gap: 2 }}>
          {yearOptions.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              style={{
                padding: "7px 16px", fontSize: 13, fontWeight: 600,
                border: "none", borderRadius: 999, cursor: "pointer",
                background: year === y ? "var(--bg-card)" : "transparent",
                color: year === y ? "#0353a4" : "#64748b",
                boxShadow: year === y ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s",
              }}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
        <KpiCard
          icon={ShoppingCart}
          label="Compras netas"
          value={fmtCLP(totals.comprasNeto)}
          sub={`${totals.comprasDocs} documentos`}
          accent={COLORS.compras}
          deltaPct={delta(totals.comprasNeto, prevTotals.comprasNeto)}
        />
        <KpiCard
          icon={Receipt}
          label="Ventas netas"
          value={fmtCLP(totals.ventasNeto)}
          sub={`${totals.ventasDocs} documentos`}
          accent={COLORS.ventas}
          deltaPct={delta(totals.ventasNeto, prevTotals.ventasNeto)}
        />
        <KpiCard
          icon={Wallet}
          label={totals.ivaPagar > 0 ? "IVA a pagar" : "Remanente IVA"}
          value={fmtCLP(totals.ivaPagar || totals.remanente)}
          sub={`Débito ${fmtCLP(totals.ventasIva)} − Crédito ${fmtCLP(totals.comprasIva)}`}
          accent={totals.ivaPagar > 0 ? "#f59e0b" : "#0ea5e9"}
        />
        <KpiCard
          icon={Scale}
          label="Margen neto"
          value={fmtCLP(totals.balance)}
          sub={totals.balance >= 0 ? "Ventas superan a las compras" : "Compras superan a las ventas"}
          accent={totals.balance >= 0 ? "#059669" : "#dc2626"}
          deltaPct={delta(totals.balance, prevTotals.balance)}
        />
      </div>

      {/* Charts grid */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
        <div style={{ ...CARD, padding: 20, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>
                Compras vs Ventas
              </h3>
              <p style={{ fontSize: 12, color: "var(--text-muted)" }}>Mes a mes durante {year}</p>
            </div>
          </div>
          <div style={{ height: 280, position: "relative" }}>
            {loading && (
              <div style={{
                position: "absolute", inset: 0, background: "rgba(255,255,255,0.7)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--text-muted)", fontSize: 13, zIndex: 1,
              }}>Cargando...</div>
            )}
            <canvas ref={barRef} />
          </div>
        </div>

        <div style={{ ...CARD, padding: 20, minWidth: 0 }}>
          <div style={{ marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>
              Tendencia del margen
            </h3>
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>Ventas netas − compras netas</p>
          </div>
          <div style={{ height: 280, position: "relative" }}>
            {loading && (
              <div style={{
                position: "absolute", inset: 0, background: "rgba(255,255,255,0.7)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--text-muted)", fontSize: 13, zIndex: 1,
              }}>Cargando...</div>
            )}
            <canvas ref={lineRef} />
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div style={CARD}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-light)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>Detalle mensual</h3>
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Las barras muestran proporción dentro del año.</p>
          </div>
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--text-secondary)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, background: COLORS.compras, borderRadius: 999 }} /> Compras
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, background: COLORS.ventas, borderRadius: 999 }} /> Ventas
            </span>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfc" }}>
                {["Mes", "Compras netas", "Ventas netas", "IVA débito − crédito", "Margen"].map((h, i) => (
                  <th key={h} style={{
                    padding: "11px 14px", textAlign: i === 0 ? "left" : "right",
                    fontSize: 11, fontWeight: 600, color: "var(--text-secondary)",
                    textTransform: "uppercase", letterSpacing: 0.3,
                    borderBottom: "1px solid #eef1f6",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthlyRows.map((r) => {
                const ivaDif = r.ventasIva - r.comprasIva;
                return (
                  <tr key={r.monthIdx} style={{ transition: "background 0.1s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#fafbfc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--text-primary)", fontWeight: 500, borderBottom: "1px solid var(--border-light)" }}>
                      {MONTHS[r.monthIdx - 1]}
                    </td>
                    <td style={{ padding: "10px 14px", borderBottom: "1px solid var(--border-light)", minWidth: 200 }}>
                      <BarCell value={r.comprasNeto} max={maxMonth} color={COLORS.compras} />
                    </td>
                    <td style={{ padding: "10px 14px", borderBottom: "1px solid var(--border-light)", minWidth: 200 }}>
                      <BarCell value={r.ventasNeto} max={maxMonth} color={COLORS.ventas} />
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: ivaDif >= 0 ? "#475569" : "#64748b", textAlign: "right", borderBottom: "1px solid var(--border-light)", fontVariantNumeric: "tabular-nums" }}>
                      {fmtCLP(ivaDif)}
                    </td>
                    <td style={{
                      padding: "10px 14px", fontSize: 13, fontWeight: 600,
                      color: r.diferencia >= 0 ? "#059669" : "#dc2626",
                      textAlign: "right", borderBottom: "1px solid var(--border-light)",
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      {r.diferencia >= 0 ? "+" : ""}{fmtCLP(r.diferencia)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "var(--bg-elevated)", fontWeight: 700 }}>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text-primary)" }}>Total {year}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text-primary)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtCLP(totals.comprasNeto)}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text-primary)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtCLP(totals.ventasNeto)}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text-secondary)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtCLP(totals.ventasIva - totals.comprasIva)}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: totals.balance >= 0 ? "#059669" : "#dc2626", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {totals.balance >= 0 ? "+" : ""}{fmtCLP(totals.balance)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
